'use client';

import type { ForecastTable, PeakDayPlan } from '@slims/shared/calculations';
import { fmtNum, fmtPct } from '@slims/shared/calculations';
import type * as React from 'react';
import { Row, Stack, Text } from '../../atoms';
import { SlChart } from '../../charts';
import {
	BarsSkeleton,
	BodySkeleton,
	type CalcTraceData,
	CalcTrace,
	CardBadge,
	CardGrid,
	CardGridSpan2,
	ChartSkeleton,
	ChartSummaryRow,
	DashGrid,
	EmptyCard,
	ExplodeButton,
	type IconBgTone,
	KpiBig,
	KpiSkeleton,
	ListSkeleton,
	OverlayArea,
	PanelCard,
	PanelCardSkeleton,
	ProgressItem,
	RecommendationCard,
	SegmentedTabs,
	StatusBlock,
	TONE_VAR,
	type WidgetTone,
} from '../../molecules';
import { PageHeader } from '../../templates';

/**
 * Finestra oraria dei grafici, ore civili Europe/Rome estremi inclusi.
 *
 * Il mock v11 disegna la giornata dalle 06:00 alle 20:00: fuori da lì la
 * produzione è zero e la curva dei consumi notturni schiaccerebbe le uniche ore
 * su cui si può agire. I totali di giornata restano invece sull'intero giorno —
 * la finestra taglia il disegno, non l'energia.
 */
const HOUR_FROM = 6;
const HOUR_TO = 20;
const WINDOW_HOURS = HOUR_TO - HOUR_FROM + 1;

/** Profilo solare orario normalizzato — IEC 61724. */
const SOLAR_H = [
	0, 0, 0, 0, 0, 0.01, 0.04, 0.12, 0.28, 0.52, 0.75, 0.92, 1.0, 0.98, 0.88, 0.68, 0.4, 0.18, 0.05,
	0.01, 0, 0, 0, 0,
];
const SOLAR_SUM = SOLAR_H.reduce((sum, h) => sum + h, 0);

/** Ora civile come etichetta: `13` → `13:00`. */
const hourLabel = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

/**
 * Fondo scala delle barre del meteo. Nessuna delle sette variabili è una
 * percentuale di qualcosa: senza un massimo dichiarato la barra non direbbe
 * niente, e con un massimo preso dal dato stesso sarebbe sempre piena.
 */
const IRRADIANCE_FULL_WM2 = 1000; // irraggiamento STC, il tetto pratico a mezzogiorno
const MODULE_TEMP_FULL_C = 85; // fondo scala operativo dichiarato dai moduli
const AIR_TEMP_FULL_C = 50; // estremo estivo italiano
const WIND_FULL_KMH = 50; // oltre, il vento è un evento non una condizione

const pct = (value: number, full: number) => Math.max(0, Math.min(100, (value / full) * 100));

/**
 * Guscio per il corpo skeleton di una card che tiene il proprio `<PanelCard>`
 * reale (titolo/aside già veri, non serve sostituirli): annuncia comunque il
 * caricamento agli screen reader, come {@link PanelCardSkeleton}.
 */
function SkeletonStatus({ children }: { children: React.ReactNode }) {
	return (
		<div
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- guscio generico (`div`), stesso pattern di PanelCardSkeleton/DistributionBar.
			role="status"
			aria-label="Caricamento…"
		>
			{children}
		</div>
	);
}

/**
 * Colore dell'icona delle tre raccomandazioni, nell'ordine del mock: ambra,
 * blu, verde. È una scelta di presentazione — il cablaggio passa il contenuto,
 * non la tinta.
 */
const REC_ICON_TONES: IconBgTone[] = ['warnSoft', 'accentSoft', 'successSoft'];

const traceProiezione: CalcTraceData = {
	formula:
		'Prod_ora = POA(ora) × kWp × PR × [1 + βT·(T_mod − 25)]\nCons_ora = Σ profili di carico dei soci',
	inputs: [
		{ label: 'Curva di produzione', value: 'modello fisico sui POD di immissione', source: 'real' },
		{ label: 'Curva di consumo', value: 'profili di carico dei POD di prelievo', source: 'real' },
		{ label: 'Meteo orario', value: 'cache Open-Meteo sulla posizione della CER', source: 'real' },
	],
	norm: 'IEC 61724 - irraggiamento sul piano dei moduli (POA) e derating NOCT',
	note: 'Due giorni ora per ora: oltre le 48 h il meteo orario perde la risoluzione che serve a dire *quando* spostare un carico.',
};

const traceAccuratezza: CalcTraceData = {
	formula:
		'Accuratezza = 100 × (1 − Σ|previsto − reale| / Σreale)\nAccuracy attesa = 100 − nMAE(classe meteo)',
	inputs: [
		{
			label: 'Previsioni',
			value: "energy_readings source='predictive' (ore predette per intero)",
			source: 'real',
		},
		{ label: 'Reale', value: 'GSE, poi rilevato o bolletta', source: 'real' },
		{ label: 'Classi meteo', value: 'soleggiato / nuvoloso / transizione', source: 'norm' },
	],
	norm: 'Errore assoluto pesato (WAPE) - N = coppie (misuratore, giorno)',
	note: "Non è una MAPE: la media degli errori percentuali orari esploderebbe sulle ore a consumo quasi nullo. Il numero grande è misurato sul consuntivo; le tre classi meteo sono l'accuracy *attesa* dei giorni previsti.",
};

export interface AiForecastViewDay {
	day: number;
	/** Giorno civile 'YYYY-MM-DD' — presente anche senza dato meteo (INB-35). */
	date?: string | null;
	production: number;
	consumption: number;
	sharedEnergy: number;
	confidence: number;
}

/** Curva oraria di consumo di un giorno previsionale (INB-35). */
export interface AiForecastConsumptionDay {
	date: string;
	dayType: 'feriale' | 'festivo';
	totalKwh: number;
	/** 24 valori in kWh, indice = ora civile Europe/Rome. */
	hours: number[];
	peakHour: number;
	peakKwh: number;
}

/**
 * Previsione dei consumi della CER con profilo orario per giorno (INB-35).
 *
 * Le curve arrivano dai profili di carico dichiarati dai soci: il picco di un
 * condominio residenziale sta la sera, quello di un ufficio a metà mattina, e
 * il weekend cambia forma oltre che quota. È la differenza fra sapere *quanto*
 * consumerà la comunità e sapere *quando*, che è l'unica delle due che permette
 * di spostare un carico.
 */
export interface AiForecastConsumption {
	available: boolean;
	pods: number;
	annualKwh: number;
	days: AiForecastConsumptionDay[];
	totalKwh: number;
	source: 'member-profiles' | 'config-estimate';
}

/** Un giorno della previsione di produzione, curva oraria inclusa (INB-39). */
export interface AiForecastProductionDay {
	date: string;
	totalKwh: number;
	/** 24 valori in kWh, indice = ora civile Europe/Rome. */
	hours: number[];
	peakHour: number;
	peakKwh: number;
	/** `physics` = irraggiamento orario reale; `pvgis-baseline` = resa mensile. */
	source: 'physics' | 'pvgis-baseline';
}

/**
 * Previsione della produzione FV con profilo orario per giorno (INB-39).
 *
 * Le curve escono dal modello fisico — irraggiamento orario sul piano dei
 * moduli — e non da una campana costante riscalata sul totale del giorno: la
 * stessa forma per ogni impianto e ogni stagione diceva a tutti di accendere a
 * mezzogiorno.
 */
export interface AiForecastProduction {
	available: boolean;
	pods: number;
	kwp: number;
	days: AiForecastProductionDay[];
	totalKwh: number;
	peakDate: string | null;
}

export interface AiForecastViewWeather {
	temperatureC: number;
	cloudCoverPct: number;
	windSpeedKmh: number;
	humidityPct: number;
	/** Irraggiamento orizzontale istantaneo, W/m². */
	ghiWm2: number | null;
	/** Irraggiamento sul piano dei moduli istantaneo, W/m². */
	poaWm2: number | null;
	/** Temperatura di cella stimata col modello NOCT, °C. */
	moduleTempC: number | null;
}

/** Una delle raccomandazioni del giorno, ancora senza risposta. */
export interface AiForecastRecommendation {
	id: string;
	/** Icona lucide della raccomandazione. */
	icon: React.ComponentType<{ className?: string }>;
	/** Cosa fare. */
	label: string;
	/** Perché conviene. */
	desc: string;
	/** Quando — già formattato dal cablaggio, es. «23 giu · 13:00». */
	when: string;
}

export interface AiForecastRecommendations {
	/** Solo le pendenti: una raccomandazione risposta esce dalla card. */
	items: AiForecastRecommendation[];
	loading?: boolean;
	/** Risposta del socio: alimenta il consuntivo del risparmio adottato. */
	onAnswer: (id: string, answer: 'si' | 'no') => void;
}

/** Riga del log di audit: un misuratore nella finestra osservata. */
export interface AiForecastAccuracyMeter {
	meterId: string;
	codice: string;
	tipo: string;
	predicted: number;
	actual: number;
	biasPct: number;
	maeKwh: number;
	accuracyPct: number;
	days: number;
	hours: number;
}

/**
 * Accuratezza **misurata** del motore predittivo: previsione contro dato reale
 * già consuntivato (INB-38).
 *
 * Da non confondere con {@link ForecastTable}, che stima a priori quanto ci si
 * possa fidare dei giorni futuri a partire dalla classe meteo (INB-37). Qui non
 * si stima nulla: si confronta ciò che il modello aveva previsto con ciò che i
 * misuratori hanno poi registrato.
 */
export interface AiForecastAccuracy {
	samples: number;
	meters: number;
	days: number;
	hours: number;
	predictedKwh: number;
	actualKwh: number;
	biasPct: number | null;
	maeKwh: number | null;
	rmseKwh: number | null;
	accuracyPct: number | null;
	perMeter: AiForecastAccuracyMeter[];
}

/**
 * Vista attiva della Previsione quando più configurazioni sono selezionate:
 * `multi` è la griglia «Panoramica» (una card per configurazione), `single` è
 * la previsione completa di una configurazione sola. Stesso schema di Dashboard
 * e Monitoraggio.
 */
export type AiForecastViewMode = 'multi' | 'single';

/** Voce del picker di configurazione, in modalità singola. */
export interface AiForecastConfigPickerItem {
	id: string;
	cerNome: string;
	configNome: string;
}

/** Dati di una card della «Panoramica» — le grandezze previsionali a 7 giorni di una configurazione sola. */
export interface AiForecastConfigCardData {
	id: string;
	cerNome: string;
	configNome: string;
	comune?: string | null;
	areaCode?: string | null;
	/** Produzione prevista sui prossimi 7 giorni, kWh. */
	prodKwh: number;
	/** Consumo previsto sui prossimi 7 giorni, kWh. */
	consKwh: number;
	/** Quota di produzione prevista condivisa, 0-100. `null` senza previsione. */
	selfConsumptionPct: number | null;
}

export interface AiForecastConfigCardProps {
	data: AiForecastConfigCardData;
	onClick?: () => void;
}

function ConfigMetricCell({
	label,
	value,
	tone,
}: {
	label: string;
	value: string;
	tone: WidgetTone;
}) {
	return (
		<div className="rounded-[7px] border border-border bg-background px-[9px] py-[7px]">
			<div className="mb-0.5 text-[9px] font-extrabold uppercase tracking-wide text-text-muted">
				{label}
			</div>
			<div className="font-mono text-sm font-extrabold" style={{ color: TONE_VAR[tone] }}>
				{value}
			</div>
		</div>
	);
}

/** Card compatta di una configurazione nella «Panoramica» della Previsione: la previsione a 7 giorni, cliccabile per aprirla per intero. */
export function AiForecastConfigCard({ data, onClick }: AiForecastConfigCardProps) {
	return (
		<button
			type="button"
			onClick={onClick}
			className="w-full cursor-pointer rounded-[11px] border border-border bg-card px-3.5 py-[13px] text-left transition-[border-color,transform] duration-150 hover:-translate-y-0.5 hover:border-accent-purple"
		>
			<div className="mb-3 flex items-start justify-between gap-2">
				<div className="min-w-0">
					<div className="mb-1 truncate text-[9.5px] font-extrabold uppercase tracking-wide text-accent">
						{data.cerNome}
					</div>
					<div className="truncate text-sm font-extrabold leading-tight">{data.configNome}</div>
					<div className="mt-[3px] truncate text-[11px] text-text-muted">
						{data.comune}
						{data.areaCode && (
							<>
								{' · '}
								<span className="font-mono text-accent">{data.areaCode}</span>
							</>
						)}
					</div>
				</div>
				<span className="inline-flex flex-none items-center gap-1.5">
					<span className="h-2 w-2 animate-pulse-dot rounded-full bg-accent-green" />
					<span className="font-mono text-[9px] font-extrabold text-accent-green">7 GG</span>
				</span>
			</div>
			<div className="mb-3 grid grid-cols-2 gap-2">
				<ConfigMetricCell
					label="Produzione prevista"
					value={`${fmtNum(data.prodKwh)} kWh`}
					tone="success"
				/>
				<ConfigMetricCell
					label="Consumo previsto"
					value={`${fmtNum(data.consKwh)} kWh`}
					tone="accent"
				/>
				<ConfigMetricCell
					label="Autoconsumo previsto"
					value={data.selfConsumptionPct != null ? fmtPct(data.selfConsumptionPct) : '-'}
					tone="purple"
				/>
			</div>
			<Text as="span" variant="caption" weight="bold" tone="accent">
				clic → previsione
			</Text>
		</button>
	);
}

export interface AiForecastConfigCardSkeletonProps {
	/** Nome della CER — l'unico dato già noto (arriva dallo scope-tree, non dal fetch di questa card): resta nitido, il resto sfoca. */
	cerNome: string;
}

/** Guscio di {@link AiForecastConfigCard} in caricamento — stessa cornice/padding della card reale. */
export function AiForecastConfigCardSkeleton({ cerNome }: AiForecastConfigCardSkeletonProps) {
	return (
		<div
			className="w-full rounded-[11px] border border-border bg-card px-3.5 py-[13px] text-left"
			// oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- guscio di card generico (`div`), stesso pattern di `PanelCardSkeleton`.
			role="status"
			aria-label={`Caricamento configurazione - ${cerNome}`}
		>
			<div className="mb-3 flex items-start justify-between gap-2">
				<div className="min-w-0 flex-1">
					<div className="mb-1 truncate text-[9.5px] font-extrabold uppercase tracking-wide text-accent">
						{cerNome}
					</div>
					<BodySkeleton className="mb-1.5 h-4 w-32" />
					<BodySkeleton className="h-3 w-40" />
				</div>
				<BodySkeleton className="h-4 w-12 flex-none rounded-full" />
			</div>
			<div className="mb-3 grid grid-cols-2 gap-2">
				{Array.from({ length: 3 }).map((_, i) => (
					<div
						key={i}
						className="rounded-[7px] border border-border bg-background px-[9px] py-[7px]"
					>
						<BodySkeleton className="mb-1 h-2.5 w-20" />
						<BodySkeleton className="h-4 w-16" />
					</div>
				))}
			</div>
			<BodySkeleton className="h-3 w-28" />
		</div>
	);
}

const FORECAST_VIEW_TABS: { value: AiForecastViewMode; label: string }[] = [
	{ value: 'multi', label: 'Panoramica' },
	{ value: 'single', label: 'Configurazione singola' },
];

/** Accuracy vuota: default quando la vista è montata senza dati (modalità Panoramica). */
const EMPTY_ACCURACY: AiForecastAccuracy = {
	samples: 0,
	meters: 0,
	days: 0,
	hours: 0,
	predictedKwh: 0,
	actualKwh: 0,
	biasPct: null,
	maeKwh: null,
	rmseKwh: null,
	accuracyPct: null,
	perMeter: [],
};

/** Raccomandazioni vuote: default quando la vista è montata senza dati (modalità Panoramica). */
const EMPTY_RECOMMENDATIONS: AiForecastRecommendations = { items: [], onAnswer: () => {} };

export interface AiForecastViewProps {
	/**
	 * Giorni previsionali: la pagina disegna i primi due, oggi e domani. In
	 * modalità «Panoramica» la vista è montata senza questi dati (li portano le
	 * singole card), quindi sono tutti opzionali.
	 */
	forecast?: AiForecastViewDay[];
	forecastLoading?: boolean;
	/** Consumi previsti ora per ora, un giorno alla volta (INB-35). */
	consumptionForecast?: AiForecastConsumption | null;
	/** Produzione prevista ora per ora, con il giorno di picco (INB-39). */
	productionForecast?: AiForecastProduction | null;
	/**
	 * Confronto ora per ora fra le due curve di **oggi** (INB-39): da qui esce
	 * l'autoconsumo istantaneo, cioè quanta della produzione i soci assorbono
	 * nell'ora in cui viene prodotta.
	 */
	todayPlan?: PeakDayPlan | null;
	/** Ora civile Europe/Rome corrente: da lì in avanti la curva è una stima. */
	currentHour?: number;
	weather?: AiForecastViewWeather | null;
	weatherLoading?: boolean;
	previsioneEnabled?: boolean;
	/** Accuracy attesa per classe meteo, da cui le tre barre del modello (INB-37). */
	forecastTable?: ForecastTable | null;
	accuracy?: AiForecastAccuracy;
	accuracyLoading?: boolean;
	/** Soglia sotto la quale l'accuratezza è segnalata, in %. */
	accuracyThresholdPct?: number;
	/** Le raccomandazioni di oggi, con la risposta Sì/No. */
	recommendations?: AiForecastRecommendations;
	/**
	 * Il piano giornaliero calcolato sui dati (INB-355), al posto della card del
	 * catalogo: il catalogo fisso resta solo in modalità demo.
	 */
	recommendationsSlot?: React.ReactNode;
	showRecommendations?: boolean;
	/**
	 * Apre l'esploso per POD di un pannello. Qui il numero scomposto è una
	 * previsione e non una misura: il livello stimato è per misuratore, quindi si
	 * scompone come il resto, ma l'esploso lo dichiara.
	 */
	onExplode?: (metricId: string, kpiTotal?: number | null) => void;
	/**
	 * Vista attiva. Assente (o `configs` con un solo elemento): niente toggle,
	 * si mostra solo la previsione della configurazione — il comportamento di
	 * prima della «Panoramica».
	 */
	view?: AiForecastViewMode;
	onViewChange?: (view: AiForecastViewMode) => void;
	/** Configurazioni disponibili per il picker di modalità singola. */
	configs?: AiForecastConfigPickerItem[];
	/** Configurazione scelta nel picker, in modalità singola. */
	selectedConfigId?: string | null;
	onSelectConfig?: (id: string) => void;
	/**
	 * Contenuto della griglia «Panoramica»: una {@link AiForecastConfigCard} per
	 * configurazione, fornita dal wiring — ognuna fa il proprio fetch, la vista
	 * non sa come si ottiene.
	 */
	overviewGridSlot?: React.ReactNode;
}

/** Un giorno della pagina: le due curve orarie più i totali di giornata. */
interface ForecastViewDay {
	/** «Oggi» / «Domani». */
	label: string;
	productionHours: number[];
	consumptionHours: number[];
	productionKwh: number;
	consumptionKwh: number;
	sharedKwh: number;
	/** Ora e valore del massimo di produzione della giornata. */
	peakHour: number;
	peakKwh: number;
}

const DAY_LABELS = ['Oggi', 'Domani'];

/** Giornata senza dati: tiene in piedi il disegno finché la previsione non c'è. */
const EMPTY_DAY: ForecastViewDay = {
	label: '-',
	productionHours: [],
	consumptionHours: [],
	productionKwh: 0,
	consumptionKwh: 0,
	sharedKwh: 0,
	peakHour: 12,
	peakKwh: 0,
};

export function AiForecastView({
	forecast = [],
	forecastLoading = false,
	consumptionForecast = null,
	productionForecast = null,
	todayPlan = null,
	currentHour = 0,
	weather = null,
	weatherLoading = false,
	previsioneEnabled = true,
	forecastTable = null,
	accuracy = EMPTY_ACCURACY,
	accuracyLoading = false,
	accuracyThresholdPct = 0,
	recommendations = EMPTY_RECOMMENDATIONS,
	recommendationsSlot,
	showRecommendations = true,
	onExplode,
	view,
	onViewChange,
	configs = [],
	selectedConfigId = null,
	onSelectConfig,
	overviewGridSlot,
}: AiForecastViewProps) {
	// Il toggle Panoramica/Configurazione-singola esiste solo con più di una
	// configurazione in ambito: con una sola, la pagina è quella di prima.
	const configToggle =
		view != null && onViewChange != null && configs.length > 1 ? { view, onViewChange } : null;
	const selectedConfig = configs.find((c) => c.id === selectedConfigId) ?? null;
	const pageTitle =
		configToggle?.view === 'single' && selectedConfig ? selectedConfig.configNome : 'Previsione';
	const headerActions = configToggle ? (
		<Stack gap="sm" align="stretch">
			<SegmentedTabs
				items={FORECAST_VIEW_TABS}
				value={configToggle.view}
				onChange={configToggle.onViewChange}
				ariaLabel="Vista della previsione"
				className="mb-0"
			/>
			{configToggle.view === 'single' && onSelectConfig && (
				<select
					value={selectedConfigId ?? ''}
					onChange={(e) => onSelectConfig(e.target.value)}
					aria-label="Configurazione"
					className="min-w-60 rounded-[7px] border border-border bg-card px-2.5 py-1.5 text-[13px] text-text-primary outline-none focus:border-accent"
				>
					{configs.map((c) => (
						<option key={c.id} value={c.id}>
							{c.cerNome} · {c.configNome}
						</option>
					))}
				</select>
			)}
		</Stack>
	) : undefined;

	if (configToggle?.view === 'multi') {
		return (
			<div>
				<PageHeader size="display" title={pageTitle} actions={headerActions} />
				{/* `@container` come nella Dashboard (`DashPanel`): senza, le container
				    query `@2xl:` di `DashGrid layout="configs"` non scattano mai e le
				    card restano incolonnate. */}
				<div className="@container">
					<Text as="div" variant="label" tone="muted" className="mb-2.5">
						Configurazioni selezionate · clicca una card per la previsione
					</Text>
					<DashGrid layout="configs">{overviewGridSlot}</DashGrid>
				</div>
			</div>
		);
	}

	const productionDays = productionForecast?.available ? productionForecast.days : [];
	const consumptionDays = consumptionForecast?.available ? consumptionForecast.days : [];

	// I due giorni della pagina. La curva di produzione è quella del modello
	// fisico quando i POD di immissione sono censiti; senza, resta la campana
	// normalizzata sul totale del giorno — l'unica cosa che si sa di un impianto
	// dichiarato solo a kWp.
	const days: ForecastViewDay[] = DAY_LABELS.map((label, index) => {
		const day = forecast[index];
		const productionDay = productionDays[index] ?? null;
		const consumptionDay = consumptionDays[index] ?? null;
		const productionKwh = productionDay?.totalKwh ?? day?.production ?? 0;
		const productionHours =
			productionDay?.hours ??
			SOLAR_H.map((h) => Math.round((h / SOLAR_SUM) * productionKwh * 10) / 10);
		const consumptionHours = consumptionDay?.hours ?? new Array<number>(24).fill(0);
		let peakHour = productionDay?.peakHour ?? 0;
		if (!productionDay) {
			for (let h = 1; h < productionHours.length; h += 1) {
				if ((productionHours[h] ?? 0) > (productionHours[peakHour] ?? 0)) peakHour = h;
			}
		}
		return {
			label,
			productionHours,
			consumptionHours,
			productionKwh,
			consumptionKwh: consumptionDay?.totalKwh ?? day?.consumption ?? 0,
			sharedKwh: day?.sharedEnergy ?? 0,
			peakHour,
			peakKwh: productionDay?.peakKwh ?? productionHours[peakHour] ?? 0,
		};
	});

	const twoDayProduction = days.reduce((sum, d) => sum + d.productionKwh, 0);
	const twoDayShared = days.reduce((sum, d) => sum + d.sharedKwh, 0);

	// Giorno di picco fra i due mostrati, non fra i sette del forecast: il badge,
	// il pallino sul grafico largo e la card del profilo devono indicare lo stesso
	// giorno, e sul grafico ci sono solo oggi e domani.
	const peakDayIndex = (days[1]?.peakKwh ?? 0) > (days[0]?.peakKwh ?? 0) ? 1 : 0;
	const peakDay = days[peakDayIndex] ?? EMPTY_DAY;
	const today = days[0] ?? EMPTY_DAY;

	/** I valori della finestra 06:00–20:00 di una curva da 24 ore. */
	const windowOf = (hours: number[]) =>
		Array.from({ length: WINDOW_HOURS }, (_, i) => hours[HOUR_FROM + i] ?? 0);

	/** Etichette dell'asse: una ogni due ore, come il mock. */
	const windowLabels = Array.from({ length: WINDOW_HOURS }, (_, i) =>
		i % 2 === 0 ? String(HOUR_FROM + i).padStart(2, '0') : '',
	);
	const windowHourLabel = (i: number) => hourLabel(HOUR_FROM + (i % WINDOW_HOURS));

	// Grafico largo: i due giorni in fila, 30 punti. L'etichetta cade a metà di
	// ciascuno, dove il mock scrive «Oggi» e «Domani».
	const wideProduction = days.flatMap((d) => windowOf(d.productionHours));
	const wideConsumption = days.flatMap((d) => windowOf(d.consumptionHours));
	const wideLabels = Array.from({ length: WINDOW_HOURS * days.length }, (_, i) => {
		const middle = Math.floor(WINDOW_HOURS / 2);
		return i % WINDOW_HOURS === middle ? (days[Math.floor(i / WINDOW_HOURS)]?.label ?? '') : '';
	});
	const widePeakIndex = peakDayIndex * WINDOW_HOURS + (peakDay.peakHour - HOUR_FROM);

	// Da qui in avanti la curva di oggi è una stima: la banda parte dall'ora
	// corrente, e sta dentro la finestra disegnata.
	const todayForecastFrom = Math.max(0, Math.min(WINDOW_HOURS - 1, currentHour - HOUR_FROM));

	// «Previsione consumi»: accuratezza misurata sui soli misuratori di prelievo,
	// pesata sull'energia — un misuratore da 4 MWh non vale come uno da 40 kWh.
	const consumptionAccuracyPct = (() => {
		const meters = accuracy.perMeter.filter((m) => m.tipo !== 'injection');
		const actual = meters.reduce((sum, m) => sum + m.actual, 0);
		if (actual <= 0) return null;
		return Math.round(meters.reduce((sum, m) => sum + m.accuracyPct * m.actual, 0) / actual);
	})();

	const accuracyByClass = (weatherClass: 'sunny' | 'cloudy' | 'transition') =>
		forecastTable?.accuracy.find((entry) => entry.weatherClass === weatherClass) ?? null;

	const accuracyDelta =
		accuracy.accuracyPct === null ? null : accuracy.accuracyPct - accuracyThresholdPct;

	if (!previsioneEnabled) {
		return (
			<div>
				<PageHeader size="display" title={pageTitle} actions={headerActions} />
				<Row justify="center">
					<EmptyCard
						icon={<span>🔒</span>}
						message={
							<>
								<Text as="span" weight="bold" tone="muted">
									Previsione AI disabilitata
								</Text>
								<br />
								Attivalo in Il mio profilo → Notifiche automatiche per abilitare questa
								funzionalità.
							</>
						}
					/>
				</Row>
			</div>
		);
	}

	return (
		<div>
			<PageHeader size="display" title={pageTitle} actions={headerActions} />

			{forecastLoading ? (
				<CardGrid columns={3}>
					<CardGridSpan2>
						<PanelCardSkeleton fill title="Forecast 2gg - Produzione FV vs Consumo" badge>
							<ChartSkeleton height={470} />
						</PanelCardSkeleton>
					</CardGridSpan2>
					<div className="flex h-full min-w-0 flex-col gap-5">
						<PanelCardSkeleton fill title="Profilo Orario · Giorno di picco">
							<ChartSkeleton height={186} />
						</PanelCardSkeleton>
						<PanelCardSkeleton fill title="Profilo Orario · Oggi">
							<ChartSkeleton height={186} />
						</PanelCardSkeleton>
					</div>
				</CardGrid>
			) : (
				<CardGrid columns={3}>
					{/* Forecast 2gg — le due curve ora per ora, oggi e domani */}
					<CardGridSpan2>
						<PanelCard
							fill
							title="Forecast 2gg - Produzione FV vs Consumo"
							aside={
								<Row gap="sm">
									<CalcTrace data={traceProiezione} />
									<ExplodeButton
										label="Esplodi la produzione prevista per POD"
										onClick={() => onExplode?.('forecast_produzione_7gg', twoDayProduction)}
										disabled={!onExplode}
									/>
									<CardBadge tone="warn">
										{peakDay.peakKwh > 0
											? `Giorno di picco: ${peakDay.label.toUpperCase()} · ~${fmtNum(peakDay.peakKwh)} kW @ ${hourLabel(peakDay.peakHour)}`
											: 'Giorno di picco: ND'}
									</CardBadge>
								</Row>
							}
						>
							{/* `fill` + `fillHeight`, non `height={470}`: la card è alta quanto
							    la colonna dei due "Profilo Orario" a fianco (`PanelCard fill`
							    su entrambi i lati della `CardGrid`), ma un numero fisso non
							    seguiva quell'altezza — lasciava un vuoto sotto la didascalia
							    ogni volta che la colonna a fianco era più alta di 470px.
							    Stesso pattern già usato in Monitoraggio per "Produzione FV vs
							    Energia Condivisa". */}
							<OverlayArea
								fill
								overlay={
									twoDayProduction === 0
										? 'In attesa dati - configura kWp o integra misuratori GSE'
										: undefined
								}
							>
								<SlChart
									series={[
										{ name: 'Produzione', color: 'var(--color-series-2)', vals: wideProduction },
										{ name: 'Consumo', color: 'var(--color-series-1)', vals: wideConsumption },
									]}
									x={wideLabels}
									hoverX={(i) =>
										`${days[Math.floor(i / WINDOW_HOURS)]?.label ?? ''} ${windowHourLabel(i)}`
									}
									unit="kW"
									fillHeight
									peak={widePeakIndex}
									forecastFrom={0}
									ariaLabel="Produzione e consumo previsti nelle prossime 48 ore"
									emptyMessage="Nessuna previsione disponibile"
								/>
								<ChartSummaryRow>
									<Text as="span" variant="inherit" tone="muted">
										2 giorni:{' '}
										<Text as="strong" variant="inherit" tone="primary">
											{fmtNum(twoDayProduction)} kWh
										</Text>
									</Text>
									<Text as="span" variant="inherit" tone="muted">
										Condivisa stim.:{' '}
										<Text as="strong" variant="inherit" tone="primary">
											{fmtNum(twoDayShared)} kWh
										</Text>
									</Text>
								</ChartSummaryRow>
							</OverlayArea>
						</PanelCard>
					</CardGridSpan2>

					{/* I due profili orari, impilati accanto al grafico largo */}
					{/* I due profili si dividono l'altezza della riga: insieme finiscono
				    dove finisce il grafico largo, come nel mock. */}
					<div className="flex h-full min-w-0 flex-col gap-5">
						<PanelCard
							fill
							title="Profilo Orario · Giorno di picco"
							aside={
								<ExplodeButton
									label="Esplodi il profilo del giorno di picco per POD"
									onClick={() => onExplode?.('forecast_profilo_orario', peakDay.productionKwh)}
									disabled={!onExplode}
								/>
							}
						>
							<SlChart
								series={[
									{
										name: 'Produzione',
										color: 'var(--color-series-2)',
										vals: windowOf(peakDay.productionHours),
									},
									{
										name: 'Consumo',
										color: 'var(--color-series-1)',
										vals: windowOf(peakDay.consumptionHours),
									},
								]}
								x={windowLabels}
								hoverX={windowHourLabel}
								unit="kW"
								height={186}
								peak={peakDay.peakHour - HOUR_FROM}
								ariaLabel={`Profilo orario del giorno di picco (${peakDay.label})`}
								emptyMessage="Nessuna curva disponibile"
							/>
							<ChartSummaryRow compact>
								<Text as="span" variant="inherit" tone="muted">
									Peak:{' '}
									<Text as="strong" variant="inherit" tone="primary">
										~{fmtNum(peakDay.peakKwh)} kW
									</Text>{' '}
									@ {hourLabel(peakDay.peakHour)}
								</Text>
								<Text as="span" variant="inherit" tone="muted">
									Prod.:{' '}
									<Text as="strong" variant="inherit" tone="primary">
										{fmtNum(peakDay.productionKwh)} kWh
									</Text>
								</Text>
								<Text as="span" variant="inherit" tone="muted">
									Cons.:{' '}
									<Text as="strong" variant="inherit" tone="primary">
										{fmtNum(peakDay.consumptionKwh)} kWh
									</Text>
								</Text>
							</ChartSummaryRow>
						</PanelCard>

						<PanelCard
							fill
							title="Profilo Orario · Oggi"
							aside={
								<ExplodeButton
									label="Esplodi il profilo di oggi per POD"
									onClick={() => onExplode?.('forecast_profilo_orario', today.productionKwh)}
									disabled={!onExplode}
								/>
							}
						>
							<SlChart
								series={[
									{
										name: 'Produzione',
										color: 'var(--color-series-2)',
										vals: windowOf(today.productionHours),
									},
									{
										name: 'Consumo',
										color: 'var(--color-series-1)',
										vals: windowOf(today.consumptionHours),
									},
								]}
								x={windowLabels}
								hoverX={windowHourLabel}
								unit="kW"
								height={186}
								peak={today.peakHour - HOUR_FROM}
								forecastFrom={todayForecastFrom}
								ariaLabel="Profilo orario di oggi, con la parte ancora da prevedere"
								emptyMessage="Nessuna curva disponibile"
							/>
							<Text as="div" variant="micro" tone="muted" className="mt-5">
								Ora{' '}
								<Text as="strong" variant="inherit" tone="primary">
									{hourLabel(currentHour)}
								</Text>{' '}
								· da qui in avanti stima AI
								{todayPlan?.available && (
									<>
										{' '}
										· autoconsumo istantaneo{' '}
										<Text as="strong" variant="inherit" tone="primary">
											{todayPlan.selfConsumptionPct}%
										</Text>
									</>
								)}
							</Text>
						</PanelCard>
					</div>
				</CardGrid>
			)}

			{/* Senza raccomandazioni (CER con l'AI spenta, o ruolo non admin) le due
			    card rimaste prendono la riga intera, invece di lasciare un buco a destra. */}
			<CardGrid columns={showRecommendations ? 3 : 2}>
				{/* Meteo Live — le sette variabili che spiegano la curva del giorno */}
				{/* Nessun badge di stato in testata: la freschezza del dato la dice il
				    titolo, e l'assenza la dice il riquadro vuoto. */}
				<PanelCard title="Meteo Live">
					{weatherLoading ? (
						<SkeletonStatus>
							<BarsSkeleton rows={7} />
						</SkeletonStatus>
					) : weather ? (
						<>
							<ProgressItem
								tight
								label="☀️ Irraggiamento GHI"
								valueLabel={weather.ghiWm2 !== null ? `${fmtNum(weather.ghiWm2)} W/m²` : 'N/D'}
								tone="warn"
								pct={weather.ghiWm2 !== null ? pct(weather.ghiWm2, IRRADIANCE_FULL_WM2) : 0}
							/>
							<ProgressItem
								tight
								label="☀️ Irraggiamento POA"
								valueLabel={weather.poaWm2 !== null ? `${fmtNum(weather.poaWm2)} W/m²` : 'N/D'}
								tone="warn"
								pct={weather.poaWm2 !== null ? pct(weather.poaWm2, IRRADIANCE_FULL_WM2) : 0}
							/>
							<ProgressItem
								tight
								label="🌡️ T° moduli (NOCT)"
								valueLabel={weather.moduleTempC !== null ? `${weather.moduleTempC}°C` : 'N/D'}
								tone="accent"
								pct={
									weather.moduleTempC !== null ? pct(weather.moduleTempC, MODULE_TEMP_FULL_C) : 0
								}
							/>
							<ProgressItem
								tight
								label="🌡️ T° ambiente"
								valueLabel={`${weather.temperatureC}°C`}
								tone="accent"
								pct={pct(weather.temperatureC, AIR_TEMP_FULL_C)}
							/>
							<ProgressItem
								tight
								label="☁️ Nuvolosità"
								valueLabel={`${weather.cloudCoverPct}%`}
								tone="muted"
								pct={weather.cloudCoverPct}
							/>
							<ProgressItem
								tight
								label="💨 Vento"
								valueLabel={`${weather.windSpeedKmh} km/h`}
								tone="success"
								pct={pct(weather.windSpeedKmh, WIND_FULL_KMH)}
							/>
							<ProgressItem
								tight
								label="💧 Umidità relativa"
								valueLabel={`${weather.humidityPct}%`}
								tone="muted"
								pct={weather.humidityPct}
							/>
						</>
					) : (
						<StatusBlock
							kind="empty"
							size="sm"
							message="Meteo non disponibile - verifica connessione"
						/>
					)}
				</PanelCard>

				{/* Raccomandazioni AI — il piano giornaliero sui dati, o le pendenti
				    del catalogo dimostrativo con la risposta Sì/No */}
				{showRecommendations && recommendationsSlot}
				{showRecommendations && !recommendationsSlot && (
					<PanelCard title="Raccomandazioni AI - Oggi">
						{recommendations.loading ? (
							<SkeletonStatus>
								<ListSkeleton rows={3} avatar={false} />
							</SkeletonStatus>
						) : recommendations.items.length === 0 ? (
							<StatusBlock
								kind="empty"
								size="sm"
								message="Hai risposto a tutte le raccomandazioni di oggi."
							/>
						) : (
							<div className="flex flex-col gap-2.5">
								{recommendations.items.map((rec, index) => {
									const Icon = rec.icon;
									const tone = REC_ICON_TONES[index % REC_ICON_TONES.length];
									return (
										<RecommendationCard
											key={rec.id}
											icon={<Icon className="h-3.5 w-3.5" />}
											{...(tone ? { iconTone: tone } : {})}
											title={rec.label}
											when={rec.when}
											desc={rec.desc}
											onAnswer={(answer) => recommendations.onAnswer(rec.id, answer)}
										/>
									);
								})}
							</div>
						)}
					</PanelCard>
				)}

				{/* Accuratezza ML — il consuntivo misurato e l'attesa per classe meteo */}
				<PanelCard
					title="Accuratezza ML"
					aside={
						<Row gap="sm">
							<CalcTrace data={traceAccuratezza} />
							<ExplodeButton
								label="Esplodi l'accuratezza del modello per POD"
								onClick={() => onExplode?.('forecast_accuratezza', null)}
								disabled={!onExplode}
							/>
						</Row>
					}
				>
					{accuracyLoading ? (
						<SkeletonStatus>
							<KpiSkeleton />
							<div className="mt-3">
								<BarsSkeleton rows={4} />
							</div>
						</SkeletonStatus>
					) : (
						<>
							<KpiBig
								value={accuracy.accuracyPct !== null ? `${accuracy.accuracyPct}%` : 'ND'}
								tone="success"
								unit="accuratezza media"
								trend={
									accuracyDelta === null
										? 'Nessuna previsione ancora confrontata con un dato reale'
										: `${accuracyDelta >= 0 ? '↑ +' : '↓ −'}${Math.abs(Math.round(accuracyDelta * 10) / 10)}% vs soglia ${accuracyThresholdPct}%`
								}
								trendTone={accuracyDelta === null ? 'muted' : accuracyDelta >= 0 ? 'up' : 'down'}
							/>
							<ProgressItem
								label="Giorni soleggiati"
								valueLabel={
									accuracyByClass('sunny') ? `${accuracyByClass('sunny')?.avgConfidencePct}%` : 'ND'
								}
								tone="success"
								pct={accuracyByClass('sunny')?.avgConfidencePct ?? 0}
							/>
							<ProgressItem
								label="Giorni nuvolosi"
								valueLabel={
									accuracyByClass('cloudy')
										? `${accuracyByClass('cloudy')?.avgConfidencePct}%`
										: 'ND'
								}
								tone="warn"
								pct={accuracyByClass('cloudy')?.avgConfidencePct ?? 0}
							/>
							<ProgressItem
								label="Transizioni rapide"
								valueLabel={
									accuracyByClass('transition')
										? `${accuracyByClass('transition')?.avgConfidencePct}%`
										: 'ND'
								}
								tone="purple"
								pct={accuracyByClass('transition')?.avgConfidencePct ?? 0}
							/>
							<ProgressItem
								tight
								label="Previsione consumi"
								valueLabel={consumptionAccuracyPct !== null ? `${consumptionAccuracyPct}%` : 'ND'}
								tone="accent"
								pct={consumptionAccuracyPct ?? 0}
							/>
						</>
					)}
				</PanelCard>
			</CardGrid>
		</div>
	);
}
